<template>
    <div class="related-link-template">
        <div class="related-link">
            <div>
                <a :href="form.url" :target="form.isExternal ? '_blank' : null">
                    <span v-if="props.filter" v-html="highlightedText"></span>
                    <span v-else>{{ form.text }}</span>
                </a>
                <div v-show="form.description">{{ form.description }}</div>
            </div>
            <div v-if="IsEditor" class="admin-control link-admin">
                <i class="fa-regular fa-2x fa-eye-slash me-1" v-show="isHidden" v-tooltip="'Link is hidden because it is not currently within the date range.'"></i>
                <i class="fa-regular fa-2x fa-pen-to-square me-1" v-show="!editing" role="button" @click="editing = true"></i>
                <i class="fa-regular fa-2x fa-square-check text-success me-1" v-show="editing" role="button" @click="changeLink"></i>
                <i class="fa-regular fa-2x fa-arrows-up-down-left-right drag-link me-1" v-if="showDrag"></i>
                <i class="fa-solid fa-2x fa-circle-minus text-danger me-1" role="button" @click="$emit('remove')"></i>
            </div>
        </div>

        <div class="alert alert-dismissible small" :class="'alert-' + alertClass" v-show="alertMessage">
            <span v-html="alertMessage"></span>
            <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close" @click="alertMessage = null"></button>
        </div>

        <div class="edit-related-link card" v-if="IsEditor && editing">
            <div class="card-body">
                <div class="form-group">
                    <label>Link Text</label>
                    <input v-model="form.text" type="text" class="form-control" :class="{ 'is-invalid': v$.text.$error }"
                           placeholder="Go to..." />
                    <span class="invalid-feedback" v-if="v$.text.$error">Link text is required</span>
                </div>
                <div class="form-group">
                    <label>Links Description</label>
                    <input v-model="form.description" type="text" class="form-control" placeholder="Description" />
                </div>
                <div class="form-group">
                    <label>Link URL</label>
                    <div :class="{ 'input-group': showReplaceButton }" v-if="!replacePreviewUrl">
                        <input v-model="form.url" type="text" class="form-control" :class="{ 'is-invalid': v$.url.$error }"
                               placeholder="http://www.example.com/somewhere or /localpath" />
                        <button v-show="showReplaceButton" class="btn btn-primary" type="button" @click="$emit('set-replace')">Replace</button>
                        <span class="invalid-feedback" v-if="v$.url.$error">Link URL is required</span>
                    </div>
                    <div v-if="replacePreviewUrl">
                        <div><small>{{ form.url }}</small></div>
                        <div class="replace-preview">
                            <a :href="replacePreviewUrl" target="_blank">Preview</a>
                            <div>
                                <button class="btn btn-sm btn-success me-1" @click="confirmReplace" :disabled="replacing">Replace</button>
                                <button class="btn btn-sm btn-danger me-1" type="button" @click="revertReplace" :disabled="replacing">Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="checkbox">
                    <label>
                        <input type="checkbox" v-model="form.isExternal" /> Open a new window on click
                    </label>
                </div>
                <div class="form-group">
                    <label>Start Date</label>
                    <VueDatePicker v-model="form.start" auto-apply format="MM/dd/yyyy, HH:mm" model-type="yyyy-MM-dd'T'HH:mm:ss"></VueDatePicker>
                </div>
                <div class="form-group">
                    <label class="form-label">End Date</label>
                    <VueDatePicker v-model="form.end" auto-apply format="MM/dd/yyyy, HH:mm" model-type="yyyy-MM-dd'T'HH:mm:ss"
                                   :class="{ 'is-invalid': v$.end.$error }"></VueDatePicker>
                    <span class="invalid-feedback" v-if="v$.end.$error">Start date must be before end date</span>
                </div>
                <div>
                    <button class="btn btn-sm btn-success me-1" @click="changeLink">Accept</button>
                    <button class="btn btn-sm btn-danger me-1" @click="revert">Cancel</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { reactive, ref, computed, watchEffect } from 'vue'
    import dayjs from 'dayjs'
    import VueDatePicker from '@vuepic/vue-datepicker'
    import '@vuepic/vue-datepicker/dist/main.css'
    import { filterRegex } from './helpers';

    import useVuelidate from '@vuelidate/core'
    import { required, helpers } from '@vuelidate/validators'
    import { endAfterStart } from '../validators/dateValidators'

    // Props
    const props = defineProps({
        Id: [String, Number],
        Url: String,
        Text: String,
        Description: String,
        IsExternal: Boolean,
        Start: [String, null],
        End: [String, null],
        showDrag: Boolean,
        Widgets: [Array, null],
        IsEditor: Boolean,
        CdnUrl: String,
        replacing: Boolean,
        filter: {
            type: String,
            default: ''
        }
    })

    // Emits
    const emit = defineEmits([
        'remove',
        'change-link',
        'set-replace',
        'confirm-replace',
        'revert-replace'
    ])

    // State
    const form = reactive({
        id: props.Id,
        text: props.Text,
        description: props.Description,
        url: props.Url,
        isExternal: props.IsExternal,
        start: props.Start,
        end: props.End
    })

    const editing = ref(false)
    const replacePreviewUrl = ref(null)
    const alertMessage = ref(null)
    const alertClass = ref(null)

    // Vuelidate
    const rules = computed(() => ({
        text: { required },
        url: { required },
        end: {
            endAfterStart: endAfterStart(form.start)
        }
    }))

    const v$ = useVuelidate(rules, form)

    // Computed

    const dirty = computed(() =>
        form.text !== props.Text ||
        form.description !== props.Description ||
        form.url !== props.Url ||
        form.isExternal !== props.IsExternal ||
        form.start !== props.Start ||
        form.end !== props.End
    )

    const isCdnUrl = computed(() =>
        form.url && props.CdnUrl && form.url.startsWith(props.CdnUrl)
    )

    const showReplaceButton = computed(() =>
        isCdnUrl.value && !replacePreviewUrl.value && props.Url === form.url
    )

    const highlightRegex = computed(() => filterRegex(props.filter))

    const highlightedText = computed(() => {
        if (!highlightRegex.value) return form.text
        return form.text.replace(highlightRegex.value, '<span class="highlight">$1</span>')
    })

    const isHidden = computed(() => {
        const now = dayjs()

        if (form.start && now.isBefore(dayjs(form.start))) return true
        if (form.end && now.isAfter(dayjs(form.end))) return true

        return false
    })


    // Watchers

    watchEffect(() => {
        if (!editing.value) {
            Object.assign(form, {
                id: props.Id,
                text: props.Text,
                description: props.Description,
                url: props.Url,
                isExternal: props.IsExternal,
                start: props.Start,
                end: props.End
            })
        }
    })

    // Methods

    async function changeLink() {
        const isValid = await v$.value.$validate()
        if (!isValid) return

        emit('change-link', {
            text: form.text,
            description: form.description,
            url: form.url,
            isExternal: form.isExternal,
            start: form.start || null,
            end: form.end || null
        })

        editing.value = false
    }

    function setReplacePreviewUrl(urlVal) {
        replacePreviewUrl.value = urlVal
    }

    function confirmReplace() {
        if (!props.replacing) {
            emit('confirm-replace')
        }
    }

    function revertReplace() {
        if (!props.replacing) {
            emit('revert-replace')
        }
    }

    function revert() {
        editing.value = false
        v$.value.$reset()
    }

    function setAlertMessage(message, alertClassVal) {
        alertMessage.value = message
        alertClass.value = alertClassVal
    }

    defineExpose({
        setReplacePreviewUrl,
        setAlertMessage
    })
</script>